import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate, formatDateTime } from '../../lib/utils';
import { Search, FileText, Eye, Clock, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

export const ManagerApprovalsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      // Fetch all vouchers and filter on frontend
      const data = await vouchersApi.getAll();
      setVouchers(data);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  // Filter vouchers based on status and user role
  const getFilteredVouchers = () => {
    let filtered = vouchers;

    // Apply status filter
    if (statusFilter === 'pending') {
      // Show vouchers pending approval from this manager
      // Either as first manager (SUBMITTED) or additional manager (PENDING_ADDITIONAL_APPROVAL)
      filtered = vouchers.filter(v => 
        (v.status === 'SUBMITTED' && v.assignedManagerId === user?.id) ||
        (v.status === 'PENDING_ADDITIONAL_APPROVAL' && v.additionalManagerId === user?.id)
      );
    } else if (statusFilter !== 'all') {
      filtered = vouchers.filter(v => v.status === statusFilter);
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(v =>
        v.beneficiaryName?.toLowerCase().includes(searchLower) ||
        v.createdByName?.toLowerCase().includes(searchLower) ||
        v.department?.toLowerCase().includes(searchLower) ||
        v.voucherNo?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const filteredVouchers = getFilteredVouchers();
  
  // Count pending approvals
  const pendingCount = vouchers.filter(v => 
    (v.status === 'SUBMITTED' && v.assignedManagerId === user?.id) ||
    (v.status === 'PENDING_ADDITIONAL_APPROVAL' && v.additionalManagerId === user?.id)
  ).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manager-approvals-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">
          {statusFilter === 'pending' ? 'Pending Approvals' : 'Employee Vouchers'}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {statusFilter === 'pending' 
            ? `Review and approve vouchers (${pendingCount} pending)`
            : 'View all vouchers from your team members'}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by beneficiary, employee, department..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-56" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">
                  Pending Approval {pendingCount > 0 && `(${pendingCount})`}
                </SelectItem>
                <SelectItem value="SUBMITTED">Awaiting First Approval</SelectItem>
                <SelectItem value="PENDING_ADDITIONAL_APPROVAL">Awaiting Second Approval</SelectItem>
                <SelectItem value="HOLD">On Hold</SelectItem>
                <SelectItem value="MANAGER_APPROVED">Manager Approved</SelectItem>
                <SelectItem value="MANAGER_REJECTED">Manager Rejected</SelectItem>
                <SelectItem value="ADDITIONAL_MANAGER_REJECTED">2nd Manager Rejected</SelectItem>
                <SelectItem value="INTERNAL_AUDIT_APPROVED">Audit Approved</SelectItem>
                <SelectItem value="ACCOUNTS_APPROVED">Accounts Approved</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
                <SelectItem value="all">All Vouchers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card>
        <CardContent className="p-0">
          {filteredVouchers.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No vouchers found</h3>
              <p className="text-muted-foreground text-sm">
                {search ? 'Try adjusting your search' : 
                  statusFilter === 'pending' ? 'All vouchers have been reviewed' : 'No vouchers in this status'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Voucher No</TableHead>
                    <TableHead className="font-semibold">Submitted By</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Beneficiary</TableHead>
                    <TableHead className="font-semibold">Department</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVouchers.map((voucher) => {
                    const isFirstApproval = voucher.status === 'SUBMITTED' && voucher.assignedManagerId === user?.id;
                    const isSecondApproval = voucher.status === 'PENDING_ADDITIONAL_APPROVAL' && voucher.additionalManagerId === user?.id;
                    
                    return (
                      <TableRow
                        key={voucher.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/approvals/${voucher.id}`)}
                        data-testid={`voucher-row-${voucher.id}`}
                      >
                        <TableCell className="font-mono font-medium">
                          <div className="flex items-center gap-1">
                            {voucher.voucherNo || <span className="text-muted-foreground">-</span>}
                            {voucher.attachments && voucher.attachments.length > 0 && (
                              <Paperclip className="w-3.5 h-3.5 text-[#6a5da4]" title={`${voucher.attachments.length} attachment(s)`} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{voucher.createdByName}</p>
                        </TableCell>
                        <TableCell>{formatDate(voucher.date)}</TableCell>
                        <TableCell>{voucher.beneficiaryName}</TableCell>
                        <TableCell>{voucher.department}</TableCell>
                        <TableCell className="font-mono">
                          {formatCurrency(voucher.amountTotal, voucher.currency)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={voucher.status} size="sm" />
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/approvals/${voucher.id}`)}
                            data-testid={`review-voucher-${voucher.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" /> 
                            {(isFirstApproval || isSecondApproval) ? 'Review' : 'View'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagerApprovalsPage;
